import { useMemo, useState } from 'react'
import { AlertTriangle, Database, KeyRound, Lock, Trash2, Users } from 'lucide-react'
import type { Currency } from '@/types/domain'
import { clearRates, rateCount } from '@/core/storage/rate-cache'
import { wipeAll } from '@/core/storage/schema-version'
import { useSettingsStore } from '@/core/storage/zustand-store'
import { useAutocomplete } from '@/hooks/useAutocomplete'
import { useEntryMutations } from '@/hooks/useEntryMutations'
import { Button } from '../shadcn/button'
import { Input } from '../shadcn/input'
import { Label } from '../shadcn/label'
import { Separator } from '../shadcn/separator'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../shadcn/sheet'
import { Switch } from '../shadcn/switch'

export interface SettingsDrawerProps {
  open: boolean
  onClose: () => void
  reload?: () => void
  wipe?: () => void
  onExportBeforeWipe?: () => void
}

export function SettingsDrawer({
  open,
  onClose,
  reload,
  wipe,
  onExportBeforeWipe,
}: SettingsDrawerProps) {
  const settings = useSettingsStore((s) => s.settings)
  const patch = useSettingsStore((s) => s.patch)

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-lg">
        <SheetHeader className="border-b border-border px-6 py-4">
          <SheetTitle className="font-display text-2xl font-normal tracking-tight">
            Configurações
          </SheetTitle>
          <SheetDescription>
            Privacidade, padrões e dados deste navegador.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-8 overflow-y-auto px-6 py-6">
          <PrivacySection
            paranoidEnabled={settings.paranoidMode.enabled}
            ttlDays={settings.paranoidMode.ttlDays}
            lastAccessedAt={settings.lastAccessedAt}
            onTogglePanic={(enabled) =>
              patch({
                paranoidMode: { enabled, ttlDays: settings.paranoidMode.ttlDays },
              })
            }
            onChangeTTL={(days) =>
              patch({
                paranoidMode: { enabled: settings.paranoidMode.enabled, ttlDays: days },
              })
            }
          />

          <Separator />

          <DefaultsSection
            defaultCurrency={settings.defaultCurrency}
            onChange={(c) => patch({ defaultCurrency: c })}
          />

          <Separator />

          <PagadorRenameSection />

          <Separator />

          <CacheSection />

          <Separator />

          <DangerSection
            reload={reload}
            wipe={wipe}
            onExportBeforeWipe={onExportBeforeWipe}
          />
        </div>
      </SheetContent>
    </Sheet>
  )
}

function SectionHeader({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode
  title: string
  description?: string
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="bg-muted text-muted-foreground flex h-8 w-8 shrink-0 items-center justify-center rounded-md">
        {icon}
      </div>
      <div>
        <h3 className="text-base font-semibold leading-tight">{title}</h3>
        {description ? (
          <p className="text-muted-foreground text-[12px]">{description}</p>
        ) : null}
      </div>
    </div>
  )
}

function PrivacySection({
  paranoidEnabled,
  ttlDays,
  lastAccessedAt,
  onTogglePanic,
  onChangeTTL,
}: {
  paranoidEnabled: boolean
  ttlDays: number
  lastAccessedAt: string
  onTogglePanic: (enabled: boolean) => void
  onChangeTTL: (days: number) => void
}) {
  const lastAccessLabel =
    new Date(lastAccessedAt).getTime() > 0
      ? new Date(lastAccessedAt).toLocaleString('pt-BR')
      : 'nunca'

  return (
    <section className="space-y-4">
      <SectionHeader
        icon={<Lock className="h-4 w-4" />}
        title="Privacidade"
        description={`Última abertura: ${lastAccessLabel}`}
      />

      <div className="bg-muted/40 flex items-center justify-between gap-4 rounded-md border border-border p-4">
        <div>
          <Label htmlFor="paranoid" className="text-sm font-medium">
            Modo máquina compartilhada
          </Label>
          <p className="text-muted-foreground text-[12px]">
            Apaga tudo automaticamente após X dias de inatividade.
          </p>
        </div>
        <Switch
          id="paranoid"
          checked={paranoidEnabled}
          onCheckedChange={onTogglePanic}
        />
      </div>

      {paranoidEnabled ? (
        <div className="space-y-2 pl-4">
          <Label htmlFor="ttlDays" className="text-muted-foreground text-[11px] font-medium tracking-widest2 uppercase">
            Apagar após
          </Label>
          <div className="flex items-center gap-3">
            <Input
              id="ttlDays"
              aria-label="Dias de inatividade"
              type="number"
              min={1}
              max={365}
              value={ttlDays}
              onChange={(e) => {
                const next = Number.parseInt(e.target.value, 10)
                if (Number.isFinite(next) && next >= 1) onChangeTTL(next)
              }}
              className="w-24 font-mono"
            />
            <span className="text-sm">dias de inatividade</span>
          </div>
        </div>
      ) : null}
    </section>
  )
}

function DefaultsSection({
  defaultCurrency,
  onChange,
}: {
  defaultCurrency: Currency
  onChange: (currency: Currency) => void
}) {
  return (
    <section className="space-y-4">
      <SectionHeader
        icon={<KeyRound className="h-4 w-4" />}
        title="Padrões"
        description="Atalhos pré-preenchidos em novos lançamentos."
      />
      <fieldset className="space-y-2">
        <Label className="text-muted-foreground text-[11px] font-medium tracking-widest2 uppercase">
          Moeda padrão
        </Label>
        <div className="flex gap-2">
          {(['USD', 'CAD'] as const).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onChange(c)}
              role="radio"
              aria-checked={defaultCurrency === c}
              className={[
                'flex h-9 w-20 items-center justify-center rounded-md border font-mono text-sm font-medium transition-colors',
                defaultCurrency === c
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-input bg-background hover:bg-muted',
              ].join(' ')}
            >
              {c}
            </button>
          ))}
        </div>
        <p className="text-muted-foreground text-[11px]">
          Salvar um lançamento atualiza a moeda padrão automaticamente.
        </p>
      </fieldset>
    </section>
  )
}

function PagadorRenameSection() {
  const autocomplete = useAutocomplete()
  const mutations = useEntryMutations()
  const [oldName, setOldName] = useState('')
  const [newName, setNewName] = useState('')
  const [feedback, setFeedback] = useState<string | null>(null)

  const matchCount = useMemo(
    () => mutations.countEntriesByPagador(oldName),
    [mutations, oldName],
  )
  // 'De' is allowed to be blank — useful to backfill the pagador on
  // lançamentos that currently have none. 'Para' must be non-empty.
  const canApply =
    newName.trim().length > 0 &&
    oldName.trim() !== newName.trim() &&
    matchCount > 0

  const apply = (): void => {
    const updated = mutations.renamePagador(oldName, newName)
    setFeedback(
      updated > 0
        ? `${updated} lançamento(s) atualizado(s).`
        : 'Nenhum lançamento foi alterado.',
    )
    setOldName('')
    setNewName('')
    window.setTimeout(() => setFeedback(null), 3000)
  }

  return (
    <section className="space-y-4">
      <SectionHeader
        icon={<Users className="h-4 w-4" />}
        title="Renomear pagador em massa"
        description="Atualiza o nome do pagador em todos os lançamentos correspondentes."
      />
      <div className="grid grid-cols-1 gap-3">
        <div className="space-y-1.5">
          <Label
            htmlFor="rename-old"
            className="text-muted-foreground text-[11px] font-medium tracking-widest2 uppercase"
          >
            De
          </Label>
          <Input
            id="rename-old"
            list="rename-old-options"
            value={oldName}
            onChange={(e) => setOldName(e.target.value)}
            placeholder="nome atual (vazio = sem pagador)"
          />
          <datalist id="rename-old-options">
            {autocomplete.pagadores.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
        </div>
        <div className="space-y-1.5">
          <Label
            htmlFor="rename-new"
            className="text-muted-foreground text-[11px] font-medium tracking-widest2 uppercase"
          >
            Para
          </Label>
          <Input
            id="rename-new"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="nome novo"
          />
        </div>
        <div className="flex items-center justify-between gap-3 pt-1">
          <p className="text-muted-foreground text-[12px]">
            {matchCount > 0 ? (
              oldName.trim().length > 0 ? (
                <>
                  <span className="text-foreground font-medium">{matchCount}</span>{' '}
                  lançamento(s) com este pagador serão atualizados.
                </>
              ) : (
                <>
                  <span className="text-foreground font-medium">{matchCount}</span>{' '}
                  lançamento(s) sem pagador receberão o novo nome.
                </>
              )
            ) : oldName.trim().length > 0 ? (
              'nenhum lançamento com esse pagador.'
            ) : (
              'nenhum lançamento sem pagador.'
            )}
          </p>
          <Button size="sm" onClick={apply} disabled={!canApply}>
            Aplicar
          </Button>
        </div>
        {feedback ? <p className="text-success text-[12px]">{feedback}</p> : null}
      </div>
    </section>
  )
}

function CacheSection() {
  const [count, setCount] = useState<number>(() => rateCount())

  const refresh = (): void => setCount(rateCount())
  const clear = (): void => {
    clearRates()
    refresh()
  }

  return (
    <section className="space-y-4">
      <SectionHeader
        icon={<Database className="h-4 w-4" />}
        title="Cache de cotações"
        description="Datas passadas são imutáveis — limpar não invalida cálculos salvos."
      />
      <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 p-4">
        <p className="text-sm">
          <span className="font-mono text-base font-medium">{count}</span>{' '}
          <span className="text-muted-foreground">cotações em cache</span>
        </p>
        <Button variant="outline" size="sm" onClick={clear} disabled={count === 0}>
          Limpar cache
        </Button>
      </div>
    </section>
  )
}

const CONFIRM_PHRASE = 'APAGAR'

function DangerSection({
  reload,
  wipe,
  onExportBeforeWipe,
}: {
  reload?: () => void
  wipe?: () => void
  onExportBeforeWipe?: () => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [typed, setTyped] = useState('')
  const canWipe = typed === CONFIRM_PHRASE

  const executeWipe = (): void => {
    ;(wipe ?? wipeAll)()
    if (reload) reload()
    else window.location.reload()
  }

  return (
    <section className="space-y-4">
      <SectionHeader
        icon={<AlertTriangle className="text-destructive h-4 w-4" />}
        title="Zona perigosa"
        description="Apaga todos os lançamentos, configurações e cache deste navegador."
      />
      {!expanded ? (
        <Button
          variant="outline"
          onClick={() => setExpanded(true)}
          className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 />
          Apagar tudo
        </Button>
      ) : (
        <div className="border-destructive/40 bg-destructive/5 space-y-3 rounded-md border p-4">
          <p className="text-sm">
            Antes de apagar, considere{' '}
            <button
              type="button"
              onClick={onExportBeforeWipe}
              className="text-destructive font-medium underline underline-offset-2 hover:no-underline"
            >
              exportar um backup
            </button>
            .
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-delete" className="text-[11px] font-medium tracking-widest2 uppercase">
              Digite <span className="font-mono">APAGAR</span> para confirmar
            </Label>
            <Input
              id="confirm-delete"
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              aria-label="Confirmação"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setExpanded(false)
                setTyped('')
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={executeWipe}
              disabled={!canWipe}
            >
              Apagar definitivamente
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}
